import { Card, Icon, IconButton, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { createSelector } from 'reselect';
import { IRootState } from '../../../@types/redux';
import AppFunctionsService from '../../../services/appFunctionServices';
import GeneralFunctions from '../../../store/general/functions';
import DamageFunctions from '../../../store/masterData/damage/functions';
import { IDamage } from '../../../types/model/masterData/damage';
import StandardFab from '../../customComponents/button/StandardFab';
import MaterialTable from '../../customComponents/materialTable/Table';
import DamageEditDialog from './dialog/Edit';

interface IDamageListProps extends RouteComponentProps {
    damages : Array<IDamage>;
    isLoading : boolean;
}

interface IDamageListState {
    selected : IDamage |null;
}

class DamageListComponent extends React.PureComponent<IDamageListProps, IDamageListState> {
    constructor(props : IDamageListProps) {
        super(props);
        this.state = {
            selected: null,
        };
    }

    public readonly componentDidMount = () => {
        this.load();
    };

    public readonly load = () => {
        DamageFunctions.getList();
    };

    private readonly onAddClick = () => {
        this.setState({
            selected: {
                name: '',
                isActive: true,
                localImages: [] as Array<{ name : string; path : string }>,
            } as IDamage,
        });
    };

    private readonly getData = (props : IDamageListProps) => props.damages;

    private readonly getDamages = createSelector(
        [this.getData],
        (damages) => damages.sort((a, b) => a.name.localeCompare(b.name)),
    );

    private save = async (damage : IDamage) => {
        try {
            await DamageFunctions.save(damage);

            this.setState({
                selected: null,
            });
        } catch (ex) {
            GeneralFunctions.generalShowErrorSnackbar('Error saving damage.', ex);
        }
    };

    private readonly onEditClose = (damage : IDamage | null) => {
        if (damage) {
            this.save(damage);
            return;
        }

        this.setState({
            selected: null,
        });
    };

    private readonly onRowClick = (e : React.MouseEvent<HTMLButtonElement>) => {
        const damage = this.props.damages.find(x => x.id === e.currentTarget.value);

        if (!damage) return;

        this.setState({
            selected: {
                ...damage,
            },
        });
    };

    public render = () => {
        const { selected } = this.state;
        const { isLoading } = this.props;
        const damages = this.getDamages(this.props);

        return (
            <div className={'fdc hfill mh0 mw0'}>
                <div className={'flx1 fdc ml15 mr15 mt20'}>
                    <Card className={'flx1 fdc mb90'}>
                        <MaterialTable<IDamage>
                            id='damageTable'
                            data={damages}
                            rowsPerPage={50}
                            isLoading={isLoading}
                            columns={[{
                                header: '',
                                paddingRight: 4,
                                width: 25,
                                renderCell: row => (
                                    <div className='aic'>
                                        <Tooltip title='Edit'>
                                            <div>
                                                <IconButton value={row.id} color='primary' onClick={this.onRowClick} aria-label='info' disabled={isLoading}>
                                                    <Icon>edit</Icon>
                                                </IconButton>
                                            </div>
                                        </Tooltip>
                                    </div>
                                ),
                            }, {
                                paddingRight: 4,
                                header: 'Name',
                                field: 'name',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Created By',
                                field: 'createdByName',
                                width: 175,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <Tooltip title={AppFunctionsService.formatDateTime(row.createdOn)}>
                                        <span>{row.createdByName}</span>
                                    </Tooltip>),
                            }, {
                                header: 'Updated By',
                                field: 'updatedByName',
                                width: 175,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <Tooltip title={AppFunctionsService.formatDateTime(row.updatedOn)}>
                                        <span>{row.updatedByName}</span>
                                    </Tooltip>),
                            }, {
                                header: 'Is Active?',
                                field: 'isActive',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: n => !n.isActive ? (<Icon className='cpr'>close</Icon>) : (<Icon className='cdg'>check</Icon>),
                            }]}
                        />
                    </Card>
                    <DamageEditDialog value={selected} fullWidth maxWidth='md' onClose={this.onEditClose} isLoading={isLoading} />
                    <StandardFab aria-label='add' disabled={isLoading} onClick={this.onAddClick}>
                        <AddIcon />
                    </StandardFab>
                </div>
            </div>
        );
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        damages: state.masterData.damage.damages,
        isLoading: state.masterData.damage.isLoading,
    };
};

const DamageList = connect(
    mapStateToProps,
)(DamageListComponent);

export default DamageList;